import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState, useEffect } from "react";
import {
  BarChart3,
  Calendar,
  CheckCircle,
  Circle,
  Shield,
  GraduationCap,
  Brain,
  BookOpen,
  Star,
  Award,
  Monitor,
  ArrowRight,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { Link } from "wouter";

interface CycleSummary {
  cycleStart: string;
  cycleEnd: string;
  today: string;
  daysRemaining: number | null;
  cycleProgressPct: number;
  count: number;
  totalHours: number;
  clinical: number;
  professional: number;
  educational: number;
  leadership: number;
  reflectiveCount: number;
  categoriesCovered: number;
  hasRenewalDate: boolean;
}

const CATEGORY_ITEMS = [
  { key: "clinical" as const, label: "Clinical", colour: "emerald", hcpcRef: "Standard 1 & 2" },
  { key: "professional" as const, label: "Professional", colour: "blue", hcpcRef: "Standard 3" },
  { key: "educational" as const, label: "Educational", colour: "violet", hcpcRef: "Standard 1" },
  { key: "leadership" as const, label: "Leadership", colour: "amber", hcpcRef: "Standard 4" },
];

const COLOUR_MAP: Record<string, { bg: string; bar: string; text: string; badge: string }> = {
  emerald: { bg: "bg-emerald-50", bar: "bg-emerald-500", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700" },
  blue: { bg: "bg-blue-50", bar: "bg-blue-500", text: "text-blue-700", badge: "bg-blue-100 text-blue-700" },
  violet: { bg: "bg-violet-50", bar: "bg-violet-500", text: "text-violet-700", badge: "bg-violet-100 text-violet-700" },
  amber: { bg: "bg-amber-50", bar: "bg-amber-500", text: "text-amber-700", badge: "bg-amber-100 text-amber-700" },
};

const HCPC_STANDARDS = [
  {
    id: "profile",
    standard: "Standard 1",
    label: "Maintained a CPD profile and undertaken a range of activities",
    check: (s: CycleSummary) => s.count >= 4 && s.categoriesCovered >= 2,
  },
  {
    id: "range",
    standard: "Standard 2",
    label: "CPD activities are a mixture of different types",
    check: (s: CycleSummary) => s.categoriesCovered >= 3,
  },
  {
    id: "benefit",
    standard: "Standard 3",
    label: "CPD has contributed to quality of practice and service delivery",
    check: (s: CycleSummary) => s.totalHours >= 10,
  },
  {
    id: "summary",
    standard: "Standard 4",
    label: "Can provide a written summary of CPD activities and their relevance",
    check: (s: CycleSummary) => s.reflectiveCount >= 1,
  },
];

const CPD_SECTIONS = [
  { href: "/physio/cpd", label: "CPD Activity Log", icon: GraduationCap, desc: "Log and review all CPD activities" },
  { href: "/physio/cpd/reflection", label: "Reflection Tool", icon: Brain, desc: "Gibbs & Rolfe structured reflections" },
  { href: "/physio/cpd/revalidation", label: "HCPC Revalidation", icon: Shield, desc: "Renewal date & audit checklist" },
  { href: "/physio/cpd/csp-framework", label: "CSP CPD Framework", icon: BookOpen, desc: "Four-dimension CSP tracker" },
  { href: "/physio/cpd/app-portfolio", label: "APP Portfolio", icon: Star, desc: "Advanced practitioner pillars" },
  { href: "/physio/cpd/acpr", label: "ACPR Specialist", icon: Award, desc: "Rheumatology specialist CPD" },
  { href: "/physio/cpd/icsp", label: "iCSP Learning Log", icon: Monitor, desc: "Online modules & webinars" },
];

function fmt(date: string): string {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function HcpcPortfolioTracker() {
  const [summary, setSummary] = useState<CycleSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/physio/cpd/cycle-summary")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) setSummary(d);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const maxHours = summary
    ? Math.max(summary.clinical, summary.professional, summary.educational, summary.leadership, 1)
    : 1;

  const urgency = summary?.daysRemaining != null
    ? summary.daysRemaining < 30
      ? "red"
      : summary.daysRemaining < 90
        ? "amber"
        : "green"
    : "slate";

  const urgencyBanner: Record<string, string> = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };

  return (
    <PhysioLayout title="HCPC CPD Portfolio Tracker">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <BarChart3 className="h-8 w-8 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">HCPC CPD Portfolio Tracker</h2>
              <p className="text-emerald-300 text-sm">2-year renewal cycle · Reflective entries · HCPC audit readiness</p>
            </div>
          </div>
          <p className="text-emerald-200 text-sm leading-relaxed">
            A unified view of your HCPC CPD portfolio across your full 2-year renewal cycle. Track hours, reflective entries, category breadth, and readiness against all four HCPC CPD standards.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-emerald-500 animate-spin" />
          </div>
        ) : !summary ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-800 text-sm">
            Could not load your portfolio data. Please refresh the page.
          </div>
        ) : (
          <>
            {!summary.hasRenewalDate && (
              <div className={`border rounded-2xl p-4 flex items-start gap-3 ${urgencyBanner["slate"]}`}>
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold mb-1">Set your HCPC renewal date</p>
                  <p className="text-sm">
                    Adding your renewal date unlocks accurate cycle tracking. Without it, the tracker shows the rolling 2 years up to today.{" "}
                    <Link href="/physio/cpd/revalidation">
                      <span className="underline font-medium cursor-pointer">Set it in HCPC Revalidation →</span>
                    </Link>
                  </p>
                </div>
              </div>
            )}

            {summary.daysRemaining !== null && (
              <div className={`border rounded-2xl p-4 flex items-center justify-between ${urgencyBanner[urgency]}`}>
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">HCPC Registration Renewal</p>
                    <p className="text-xs opacity-80">
                      {fmt(summary.cycleStart)} — {fmt(summary.cycleEnd)}
                    </p>
                  </div>
                </div>
                <p className="text-lg font-bold whitespace-nowrap">
                  {summary.daysRemaining > 0
                    ? `${summary.daysRemaining}d remaining`
                    : summary.daysRemaining === 0
                      ? "Due today"
                      : `${Math.abs(summary.daysRemaining)}d overdue`}
                </p>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900">Cycle Progress</h3>
                <span className="text-sm text-slate-500">
                  {fmt(summary.cycleStart)} → {fmt(summary.cycleEnd)}
                </span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-1">
                <div
                  className="h-full bg-emerald-500 rounded-full transition-all"
                  style={{ width: `${summary.cycleProgressPct}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>{summary.cycleProgressPct}% of cycle elapsed</span>
                {summary.daysRemaining !== null && summary.daysRemaining > 0 && (
                  <span>{summary.daysRemaining} days to renewal</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
                <p className="text-2xl font-bold text-emerald-800">{summary.count}</p>
                <p className="text-xs text-emerald-600 mt-0.5">Activities this cycle</p>
              </div>
              <div className="bg-teal-50 rounded-2xl p-4 border border-teal-100">
                <p className="text-2xl font-bold text-teal-800">{summary.totalHours.toFixed(1)}</p>
                <p className="text-xs text-teal-600 mt-0.5">Total CPD hours</p>
              </div>
              <div className="bg-violet-50 rounded-2xl p-4 border border-violet-100">
                <p className="text-2xl font-bold text-violet-800">{summary.reflectiveCount}</p>
                <p className="text-xs text-violet-600 mt-0.5">Reflective entries</p>
              </div>
              <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
                <p className="text-2xl font-bold text-amber-800">{summary.categoriesCovered}/4</p>
                <p className="text-xs text-amber-600 mt-0.5">Categories covered</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-4">Hours by Category</h3>
              <div className="space-y-4">
                {CATEGORY_ITEMS.map((cat) => {
                  const hours = summary[cat.key];
                  const pct = Math.round((hours / maxHours) * 100);
                  const c = COLOUR_MAP[cat.colour];
                  return (
                    <div key={cat.key}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700">{cat.label}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${c.badge}`}>
                            {cat.hcpcRef}
                          </span>
                        </div>
                        <span className={`text-sm font-bold ${c.text}`}>{hours.toFixed(1)} hrs</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${c.bar} rounded-full transition-all`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {summary.categoriesCovered < 4 && (
                <p className="text-xs text-slate-500 mt-4 bg-slate-50 rounded-xl px-3 py-2">
                  <strong>HCPC requires a range of CPD activities.</strong> You currently have coverage in {summary.categoriesCovered} of 4 categories.
                  Aim to add activities in the remaining {4 - summary.categoriesCovered} {4 - summary.categoriesCovered === 1 ? "category" : "categories"} before your renewal.
                </p>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="h-5 w-5 text-violet-600" />
                <h3 className="font-bold text-slate-900">Reflective Entries</h3>
                <Link href="/physio/cpd/reflection" className="ml-auto text-xs text-emerald-700 hover:text-emerald-800 font-medium">
                  Open Reflection Tool →
                </Link>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Reflective entries are activities where the learning need or activity description mentions "reflect", "Gibbs", or "Rolfe". HCPC Standard 4 requires you to be able to explain the relevance of your CPD — structured reflection supports this.
              </p>
              {summary.reflectiveCount === 0 ? (
                <div className="bg-violet-50 border border-violet-100 rounded-xl px-4 py-3 text-sm text-violet-800">
                  No reflective entries logged yet for this cycle.{" "}
                  <Link href="/physio/cpd/reflection">
                    <span className="underline font-medium cursor-pointer">Start a Gibbs or Rolfe reflection →</span>
                  </Link>
                </div>
              ) : (
                <div className="flex items-center gap-4 bg-violet-50 border border-violet-100 rounded-xl px-4 py-3">
                  <Brain className="h-8 w-8 text-violet-400 shrink-0" />
                  <div>
                    <p className="text-lg font-bold text-violet-800">{summary.reflectiveCount}</p>
                    <p className="text-xs text-violet-600">Reflective entries this cycle</p>
                  </div>
                  <p className="text-sm text-violet-700 ml-2">
                    {summary.reflectiveCount >= 3
                      ? "Good breadth of reflective practice evidenced."
                      : summary.reflectiveCount >= 1
                        ? "At least one reflection logged. HCPC recommend regular reflective practice throughout the cycle."
                        : ""}
                  </p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-1">HCPC CPD Standards Readiness</h3>
              <p className="text-sm text-slate-500 mb-4">
                Indicative readiness based on your logged portfolio data. Approximately 2.5% of physiotherapists are audited at each renewal.
              </p>
              <div className="space-y-3">
                {HCPC_STANDARDS.map((std) => {
                  const met = std.check(summary);
                  return (
                    <div key={std.id} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50">
                      {met ? (
                        <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <Circle className="h-5 w-5 text-slate-300 shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-semibold text-emerald-700">{std.standard}</span>
                          {met ? (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">On track</span>
                          ) : (
                            <span className="text-xs bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">Needs attention</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700">{std.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                Readiness indicators are based on: ≥4 activities + ≥2 categories (Std 1), ≥3 categories (Std 2), ≥10 hours (Std 3), ≥1 reflective entry (Std 4).
                These are indicative only — HCPC assess quality and relevance, not just quantity.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-4">CPD Portfolio Sections</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CPD_SECTIONS.map((section) => (
                  <Link key={section.href} href={section.href}>
                    <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50 transition-all cursor-pointer group">
                      <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                        <section.icon className="h-4 w-4 text-emerald-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors">{section.label}</p>
                        <p className="text-xs text-slate-500">{section.desc}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500 transition-colors shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
              <strong>HCPC CPD Standards:</strong> Physiotherapists must maintain a CPD profile, undertake a range of CPD activities, demonstrate benefit to service users, and be prepared to present a profile if audited. This tracker is a preparation aid only and does not replace formal HCPC registration renewal. Visit hcpc-uk.org for full guidance.
            </div>
          </>
        )}
      </div>
    </PhysioLayout>
  );
}
